import {
    Token,
    TokenStream,
    ParserErrorListener,
    Recognizer,
    RecognitionException,
    InputMismatchException,
    NoViableAltException,
    FailedPredicateException,
} from "antlr4ts";
import { IntervalSet } from "antlr4ts/misc";
import { XLogoSyntaxError, SyntaxErrorCause } from "./syntaxError";
import { XLogoParser } from "./parser";
import { ProcDeclRecoveryException } from "./xLogoParserErrorStrategy";

export class ANTLRSyntaxErrorListener implements ParserErrorListener {
    public errorList: XLogoSyntaxError[] = [];

    /*@Override*/
    public syntaxError<T extends Token>(
        /*@NotNull*/
        recognizer: Recognizer<T, any>,
        offendingSymbol: T | undefined,
        line: number,
        charPositionInLine: number,
        /*@NotNull*/
        msg: string,
        e: RecognitionException | undefined,
    ): void {
        // This error listener can only be used with XLogoParser
        if (!(recognizer instanceof XLogoParser)) return;

        const parser: XLogoParser = recognizer;
        let errArg = {};
        let errCause: SyntaxErrorCause | undefined = undefined;
        if (!e) {
            const expecting: IntervalSet = parser.getExpectedTokens();
            const expectedToken: string = expecting.toStringVocabulary(
                recognizer.vocabulary,
            );
            if (msg.startsWith("missing ")) {
                errCause = SyntaxErrorCause.MISSING_INPUT;
                errArg = {
                    missingToken: expectedToken,
                    missingPosition: this.getTokenErrorDisplay(offendingSymbol),
                    msg: msg.substr("missing ".length),
                };
            } else if (msg.startsWith("extraneous input ")) {
                errCause = SyntaxErrorCause.EXTRANEOUS_INPUT;
                errArg = {
                    extraneousToken: this.getTokenErrorDisplay(offendingSymbol),
                    expectedToken: expecting.toStringVocabulary(
                        recognizer.vocabulary,
                    ),
                    msg: msg.substr("extraneous input ".length),
                };
            } else {
                errCause = SyntaxErrorCause.UNKNOWN;
                errArg = { msg: msg };
            }
        } else {
            if (e instanceof InputMismatchException) {
                errCause = SyntaxErrorCause.MISMATCHED_INPUT;
                errArg = {
                    mismatchedToken: this.getTokenErrorDisplay(offendingSymbol),
                    msg: msg,
                };
            } else if (e instanceof NoViableAltException) {
                let tokens: TokenStream = recognizer.inputStream;
                let input: string;
                if (tokens) {
                    if (e.startToken.type === Token.EOF) {
                        input = "<EOF>";
                    } else {
                        input = tokens.getTextFromRange(
                            e.startToken,
                            e.getOffendingToken(),
                        );
                    }
                } else {
                    input = "<unknown input>";
                }
                errCause = SyntaxErrorCause.NO_VIABLE_ALT;
                errArg = {
                    no_viable_alt_position: input,
                    msg: msg,
                };
            } else if (e instanceof FailedPredicateException) {
                errCause = SyntaxErrorCause.FAILED_PREDICATE;
                errArg = { msg: msg };
            } else if (e instanceof ProcDeclRecoveryException) {
                errCause = SyntaxErrorCause.MISSING_END;
                errArg = {};
            } else {
                errCause = SyntaxErrorCause.UNKNOWN;
                errArg = { msg: msg };
            }
        }

        this.errorList.push(
            new XLogoSyntaxError(errCause, offendingSymbol, errArg),
        );
    }

    // Code copied from https://github.com/tunnelvisionlabs/antlr4ts/blob/master/src/DefaultErrorStrategy.ts
    protected getTokenErrorDisplay(t: Token | undefined): string {
        if (!t) {
            return "<no token>";
        }
        let s = this.getSymbolText(t);
        if (!s) {
            if (this.getSymbolType(t) === Token.EOF) {
                s = "<EOF>";
            } else {
                s = `<${this.getSymbolType(t)}>`;
            }
        }
        return this.escapeWSAndQuote(s);
    }

    protected getSymbolText(symbol: Token): string | undefined {
        return symbol.text;
    }

    protected getSymbolType(symbol: Token): number {
        return symbol.type;
    }

    protected escapeWSAndQuote(s: string): string {
        s = s.replace("\n", "\\n");
        s = s.replace("\r", "\\r");
        s = s.replace("\t", "\\t");
        return "'" + s + "'";
    }
}



// WEBPACK FOOTER //
// ./src/app/xlogo-parser/ANTLRDefaultSyntaxErrorListener.ts